<template>
  <div class="search-history-container"
       ref="container"
       @mouseenter="handleMouseEnter"
       @mouseleave="handleMouseLeave">
    <div class="search-wrapper">
      <!-- 搜索输入框 -->
      <el-input
        ref="searchInput"
        v-model="localKeyword"
        :placeholder="placeholder"
        class="search-input"
        clearable
        @focus="handleFocus"
        @blur="handleBlur"
        @keyup.enter.native="handleEnter"
        @clear="handleClear"
        @input="handleInput"
      >
        <i slot="prefix" class="el-input__icon el-icon-search search-icon"></i>
      </el-input>

      <!-- 搜索按钮插槽 -->
      <slot name="search-button" :keyword="localKeyword" :perform-search="performSearch"></slot>
    </div>

    <!-- 搜索历史下拉面板 -->
    <transition name="slide-fade">
      <div
        v-show="showHistory"
        class="search-history-dropdown"
        ref="historyDropdown"
        @mouseenter="handleDropdownMouseEnter"
        @mouseleave="handleDropdownMouseLeave"
      >
        <div class="history-container">
          <!-- 最近搜索 -->
          <div class="history-section" v-if="searchHistory.length > 0">
            <div class="history-header">
              <i class="el-icon-time history-header-icon"></i>
              <span class="history-title">最近搜索</span>
              <el-button
                type="text"
                size="mini"
                @click="clearHistory"
                class="clear-btn"
              >
                清空
              </el-button>
            </div>
            <div class="history-tags">
              <div
                v-for="(item, index) in searchHistory"
                :key="'history-'+index"
                @click="selectHistoryItem(item)"
                class="history-item"
              >
                <span class="history-text">{{ formatText(item, 8) }}</span>
              </div>
            </div>
          </div>

          <!-- 没有搜索历史提示 -->
          <div class="history-section" v-else-if="showHistory">
            <div class="history-header">
              <i class="el-icon-time history-header-icon"></i>
              <span class="history-title">最近搜索</span>
            </div>
            <div class="no-data-tip">
              <i class="el-icon-info"></i>
              <span>暂无搜索历史</span>
            </div>
          </div>

          <!-- 常用搜索 -->
          <div class="history-section" v-if="frequentSearches.length > 0">
            <div class="history-header">
              <i class="el-icon-star-on history-header-icon"></i>
              <span class="history-title">个人常用搜索</span>
            </div>
            <div class="history-tags">
              <el-tag
                v-for="(item, index) in frequentSearches"
                :key="'frequent-'+index"
                @click="selectFrequentItem(item)"
                class="history-tag frequent-tag"
                :class="getAnimatedClass(item.frequency, index)"
              >
                <span v-if="index < 3" class="animated-rank">{{ index + 1 }}</span>
                <i
                  v-if="item.frequency > 0.7"
                  class="el-icon-star animated-star"
                ></i>
                {{ formatText(item.keyword, 8) }}
                <span
                  v-if="index < 3 && item.frequency > 0.5"
                  class="sparkle"
                  :class="'sparkle-' + (index + 1)"
                ></span>
              </el-tag>
            </div>
          </div>

          <!-- 没有常用搜索提示 -->
          <div class="history-section" v-else-if="showHistory">
            <div class="history-header">
              <i class="el-icon-star-on history-header-icon"></i>
              <span class="history-title">个人常用搜索</span>
            </div>
            <div class="no-data-tip">
              <i class="el-icon-info"></i>
              <span>暂时没有个人常用搜索历史，等待稍后更新</span>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>


<script>
export default {
  name: 'SearchHistory',
  props: {
    keyword: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '请输入关键词搜索'
    },
    storageKey: {
      type: String,
      default: 'searchHistory'
    }
  },
  data() {
    return {
      localKeyword: this.keyword,
      searchHistory: [],
      // 移除本地的 searchCounts，改为从后端获取
      showHistory: false,
      shouldHidePanel: true,
      // 添加缓存数据
      cachedFrequentSearches: null,
      cacheTimestamp: null,
      hoverTimer: null, // 鼠标悬停定时器
      hideTimer: null   // 隐藏面板定时器
    }
  },
  computed: {
    // 计算常用搜索（从后端获取的数据）
    frequentSearches() {
      // 检查是否有有效的缓存
      if (this.isCacheValid()) {
        return this.cachedFrequentSearches;
      }

      // 如果没有有效缓存，尝试从本地存储获取
      const cachedData = this.getCacheFromLocalStorage();
      if (cachedData && this.isCacheValid(cachedData.timestamp)) {
        return cachedData.data;
      }

      // 如果都没有，则返回空数组
      return [];
    }
  },
  watch: {
    keyword(newVal) {
      this.localKeyword = newVal;
    },
    localKeyword(newVal) {
      this.$emit('update:keyword', newVal);
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    this.fetchSearchHistory();

    // 尝试从缓存或后端获取常用搜索数据
    this.loadFrequentSearches();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
    // 清除定时器
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    if (this.hideTimer) clearTimeout(this.hideTimer);
  },
  methods: {
    // 检查缓存是否有效（1小时内）
    isCacheValid(timestamp = this.cacheTimestamp) {
      if (!timestamp) return false;
      const now = new Date().getTime();
      const cacheTime = new Date(timestamp).getTime();
      // 缓存有效期1小时
      return (now - cacheTime) < (15 * 60 * 1000);
    },

    // 从本地存储获取缓存
    getCacheFromLocalStorage() {
      try {
        const cached = localStorage.getItem(`${this.storageKey}_frequent_cache`);
        return cached ? JSON.parse(cached) : null;
      } catch (error) {
        console.error('获取缓存数据失败:', error);
        return null;
      }
    },

    // 缓存常用搜索数据
    cacheFrequentSearches(data) {
      this.cachedFrequentSearches = data;
      this.cacheTimestamp = new Date().toISOString();

      // 同时保存到本地存储
      try {
        const cacheData = {
          data: data,
          timestamp: this.cacheTimestamp
        };
        localStorage.setItem(`${this.storageKey}_frequent_cache`, JSON.stringify(cacheData));
      } catch (error) {
        console.error('缓存数据失败:', error);
      }
    },

    // 从后端获取常用搜索数据的方法
    async fetchFrequentSearchesFromBackend() {
      try {
        // 从本地存储获取用户信息
        const logininfo = JSON.parse(localStorage.getItem("logininfo"));
        if (!logininfo || !logininfo.id) {
          return [];
        }

        // 调用后端接口获取个人常用搜索
        const response = await this.$http.get(`/productList/frequent-searches?userId=${logininfo.id}`);
        return response.data;
      } catch (error) {
        console.error('获取后端常用搜索数据失败:', error);
        return [];
      }
    },

    // 加载常用搜索数据
    async loadFrequentSearches() {
      // 检查是否有有效的缓存
      if (this.isCacheValid()) {
        return;
      }

      const cachedData = this.getCacheFromLocalStorage();
      if (cachedData && this.isCacheValid(cachedData.timestamp)) {
        this.cachedFrequentSearches = cachedData.data;
        this.cacheTimestamp = cachedData.timestamp;
        return;
      }

      // 从后端获取最新数据
      const backendData = await this.fetchFrequentSearchesFromBackend();

      // 转换数据格式以匹配现有逻辑
      const maxCount = Math.max(...backendData.map(item => item.count || 0), 0);
      if (maxCount === 0) return;

      const frequentData = backendData.map(item => ({
        keyword: item.keyword,
        count: item.count,
        frequency: (item.count || 0) / maxCount
      }));

      // 缓存数据
      this.cacheFrequentSearches(frequentData);
    },

    // 格式化文本，超过指定长度用省略号代替
    formatText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    // 获取动画类名
    getAnimatedClass(frequency, index) {
      const classes = [];

      // 根据频率添加样式
      if (frequency > 0.8) classes.push('animated-high');
      else if (frequency > 0.6) classes.push('animated-medium');
      else if (frequency > 0.4) classes.push('animated-low');

      // 前三名特殊标记
      if (index < 3) {
        classes.push('animated-top');
        if (index === 0) classes.push('animated-first');
      }

      return classes;
    },

    // 鼠标进入搜索容器
    handleMouseEnter() {
      // 清除隐藏定时器
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }

      // 延迟显示搜索历史面板，避免频繁切换
      this.hoverTimer = setTimeout(() => {
        this.showHistory = true;
        this.shouldHidePanel = true;
      }, 150);
    },

    // 鼠标离开搜索容器
    handleMouseLeave() {
      // 清除显示定时器
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }

      // 延迟隐藏搜索历史面板
      this.hideTimer = setTimeout(() => {
        if (this.shouldHidePanel) {
          this.showHistory = false;
        }
      }, 200);
    },

    // 鼠标进入下拉面板
    handleDropdownMouseEnter() {
      // 清除隐藏定时器
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },

    // 鼠标离开下拉面板
    handleDropdownMouseLeave() {
      // 延迟隐藏搜索历史面板
      this.hideTimer = setTimeout(() => {
        if (this.shouldHidePanel) {
          this.showHistory = false;
        }
      }, 200);
    },

    handleFocus() {
      // 清除隐藏定时器
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }

      // 清除显示定时器
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }

      // 立即显示搜索历史面板
      this.showHistory = true;
      this.shouldHidePanel = true;
    },

    handleBlur() {
      // 延迟关闭，确保点击事件能正常触发
      this.hideTimer = setTimeout(() => {
        // 只有当面板不是因为清除操作而关闭时才隐藏
        if (this.shouldHidePanel) {
          this.showHistory = false;
        }
      }, 200);
    },

    handleDocumentClick(event) {
      if (this.$refs.container && !this.$refs.container.contains(event.target)) {
        this.showHistory = false;
        this.shouldHidePanel = true;
      }
    },

    handleInput() {
      // 清除隐藏定时器
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }

      // 清除显示定时器
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }

      // 立即显示搜索历史面板
      this.showHistory = true;
      this.shouldHidePanel = true;
    },

    handleClear() {
      this.localKeyword = '';
      this.$emit('clear');
      this.$emit('search', '');
      // 清除后应该显示搜索历史
      this.showHistory = true;
      this.shouldHidePanel = true;

      // 延迟重置标志位
      this.$nextTick(() => {
        setTimeout(() => {
          this.shouldHidePanel = true;
        }, 300);
      });
    },

    handleEnter() {
      this.performSearch();
    },

    performSearch() {
      const searchKeyword = this.localKeyword ? this.localKeyword.trim() : '';

      // 无论是否有内容，都应该触发搜索事件
      this.$emit('search', searchKeyword);

      // 只有当有内容时才保存到搜索历史
      if (searchKeyword) {
        this.updateSearchHistory(searchKeyword);
      }

      this.showHistory = false;
      this.shouldHidePanel = true;
    },

    selectHistoryItem(item) {
      this.localKeyword = item;
      this.$emit('search', item);
      this.showHistory = false;
      this.shouldHidePanel = true;
    },

    // 选择常用搜索项
    selectFrequentItem(item) {
      this.localKeyword = item.keyword;
      this.$emit('search', item.keyword);
      this.showHistory = false;
      this.shouldHidePanel = true;
    },

    updateSearchHistory(query) {
      if (!query || !query.trim()) return;

      const trimmedQuery = query.trim();
      const existingIndex = this.searchHistory.indexOf(trimmedQuery);

      if (existingIndex > -1) {
        this.searchHistory.splice(existingIndex, 1);
      }

      this.searchHistory.unshift(trimmedQuery);

      if (this.searchHistory.length > 8) {
        this.searchHistory = this.searchHistory.slice(0, 8);
      }

      this.saveSearchHistory();
    },

    async fetchSearchHistory() {
      try {
        const savedHistory = localStorage.getItem(this.storageKey);
        this.searchHistory = savedHistory ? JSON.parse(savedHistory) : [];
      } catch (error) {
        console.error('获取搜索历史失败:', error);
        this.searchHistory = [];
      }
    },

    saveSearchHistory() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.searchHistory));
      } catch (error) {
        console.error('保存搜索历史失败:', error);
      }
    },

    clearHistory() {
      try {
        this.searchHistory = [];
        localStorage.removeItem(this.storageKey);
      } catch (error) {
        console.error('清空搜索历史失败:', error);
        this.searchHistory = [];
      }
    }
  }
}
</script>



<style scoped>
.search-history-container {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.search-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.search-input {
  width: 280px;
}

.search-input ::v-deep .el-input__inner {
  border: 1px solid #dcdfe6;
  border-radius: 4px 0 0 4px;
  padding-left: 35px;
  height: 36px;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-sizing: border-box;
}

.search-input ::v-deep .el-input__inner:hover {
  border-color: #c0c4cc;
}

.search-input ::v-deep .el-input__inner:focus {
  border-color: #409EFF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.search-input ::v-deep .el-input__prefix {
  left: 10px;
}

.search-icon {
  color: #909399;
  transition: color 0.3s;
  font-size: 16px;
}

.search-input ::v-deep .is-focus .search-icon {
  color: #409EFF;
}

/* 搜索历史下拉面板 */
.search-history-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  z-index: 2000;
  min-width: 320px;
  overflow: hidden;
}

.history-container {
  max-height: 300px;
  overflow-y: auto;
  padding: 16px;
}

.history-section {
  margin-bottom: 16px;
}

.history-section:last-child {
  margin-bottom: 0;
}

.history-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.history-header-icon {
  font-size: 14px;
  color: #909399;
  margin-right: 8px;
}

.history-title {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  flex: 1;
}

.clear-btn ::v-deep .el-button--text {
  font-size: 12px;
  padding: 0;
  color: #909399;
}

.clear-btn ::v-deep .el-button--text:hover {
  color: #f56c6c;
}

/* 历史搜索项 */
.history-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.history-item {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  background-color: #f0f2f5;
  border: 1px solid #e4e7ed;
  font-size: 12px;
  padding: 0 10px;
  border-radius: 12px;
  height: 24px;
  display: flex;
  align-items: center;
  max-width: 100%;
}

.history-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  background-color: #ecf5ff;
  border-color: #c6e2ff;
}

.history-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-tag {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  background-color: #f0f2f5;
  border-color: #e4e7ed;
  font-size: 12px;
  padding: 0 8px;
  border-radius: 12px;
  height: 24px;
  line-height: 22px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  max-width: 100%;
}

.history-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  background-color: #ecf5ff;
  border-color: #c6e2ff;
}

.frequent-tag {
  background-color: #fdf6ec;
  border-color: #f9d9b4;
  color: #e6a23c;
  padding: 0 10px 0 6px;
  position: relative;
}

.frequent-tag:hover {
  background-color: #fdf6ec;
  border-color: #f9d9b4;
  color: #e6a23c;
}

/* 动画排名数字 */
.animated-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  color: #fff;
  background: linear-gradient(135deg, #ff8c00, #ffd700);
  margin-right: 4px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.animated-top:hover .animated-rank {
  transform: scale(1.1);
}

/* 动画星星图标 */
.animated-star {
  color: #f5d76e;
  margin-left: 4px;
  font-size: 12px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.animated-high:hover .animated-star {
  transform: rotate(15deg) scale(1.2);
  color: #ffd700;
}

/* 动画样式类 */
.animated-high {
  background: linear-gradient(135deg, #fff8e1, #fdf6ec);
  border-color: #ffecb3;
  color: #e6a23c;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.animated-medium {
  background: linear-gradient(135deg, #fffaf5, #fdf6ec);
  border-color: #f9d9b4;
  color: #e6a23c;
}

.animated-low {
  background: linear-gradient(135deg, #fdf7f0, #fdf6ec);
  border-color: #f0d4b3;
  color: #e6a23c;
}

/* 前三名样式 */
.animated-top {
  font-weight: 500;
  border-width: 1.5px;
}

.animated-first {
  background: linear-gradient(135deg, #fff3e0, #fdf6ec);
  border-color: #ffe0b2;
  box-shadow: 0 2px 6px rgba(255, 215, 0, 0.1);
}

/* 闪光特效 */
.sparkle {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.sparkle::before {
  content: '';
  position: absolute;
  top: 2px;
  right: 2px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #ffd700;
  box-shadow: 0 0 4px #ffd700;
  animation: sparkle 2s infinite;
}

.sparkle::after {
  content: '';
  position: absolute;
  top: 6px;
  right: 6px;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background-color: #ffd700;
  box-shadow: 0 0 2px #ffd700;
  animation: sparkle 1.5s infinite 0.5s;
}

.sparkle-1::before {
  background-color: #ffd700;
  box-shadow: 0 0 6px #ffd700;
  animation: sparkle 1.5s infinite;
}

.sparkle-1::after {
  background-color: #ffd700;
  box-shadow: 0 0 4px #ffd700;
  animation: sparkle 2s infinite 0.3s;
}

.sparkle-2::before {
  background-color: #c0c0c0;
  box-shadow: 0 0 4px #c0c0c0;
  animation: sparkle 2s infinite;
}

.sparkle-2::after {
  background-color: #c0c0c0;
  box-shadow: 0 0 2px #c0c0c0;
  animation: sparkle 1.8s infinite 0.4s;
}

.sparkle-3::before {
  background-color: #cd7f32;
  box-shadow: 0 0 4px #cd7f32;
  animation: sparkle 2.2s infinite;
}

.sparkle-3::after {
  background-color: #cd7f32;
  box-shadow: 0 0 2px #cd7f32;
  animation: sparkle 1.7s infinite 0.6s;
}

@keyframes sparkle {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.history-tag ::v-deep .el-tag__close {
  margin-left: 4px;
  color: #909399;
  background-color: transparent;
  width: 14px;
  height: 14px;
  line-height: 14px;
  opacity: 0.7;
  transition: all 0.2s;
}

.history-tag:hover ::v-deep .el-tag__close {
  opacity: 1;
  background-color: #c0c4cc;
  color: #fff;
}

.history-tag ::v-deep .el-tag__close:hover {
  background-color: #909399;
  color: #fff;
}

/* 动画效果 */
.slide-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.slide-fade-enter, .slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 滚动条样式 */
.history-container::-webkit-scrollbar {
  width: 6px;
}

.history-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.history-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.history-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-input {
    width: 200px;
  }

  .search-history-dropdown {
    min-width: 280px;
  }

  .history-container {
    max-height: 250px;
    padding: 12px;
  }

  .history-tag ::v-deep .el-tag {
    padding: 0 6px;
    font-size: 11px;
    height: 22px;
    line-height: 20px;
  }
}

@media (max-width: 480px) {
  .search-input {
    width: 160px;
  }

  .search-history-dropdown {
    min-width: 240px;
  }

  .history-container {
    padding: 10px;
  }
}
.no-data-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  color: #909399;
  font-size: 13px;
}

.no-data-tip i {
  margin-right: 8px;
  font-size: 16px;
}
</style>



// WEBPACK FOOTER //
// productSelectHost.vue?3b7a2f45